import React, { useState } from 'react';
import { cancelPricingSchedule, reschedulePricing, pricingScheduleError, PricingPublication } from '../../../services/individualPricingSettings';
import { pricingVersionLabel } from '../../../utils/pricingVersionLabel';
import { fromSouthAfricanDateTimeInputValue, toSouthAfricanDateTimeInputValue, formatSouthAfricanDateTime } from '../../../utils/southAfricanTime';

export default function PricingScheduleActions({ version, disabled, onBusy, onChanged }: {
  version: PricingPublication; disabled: boolean; onBusy: (busy: boolean) => void; onChanged: (message: string) => void;
}) {
  const [action, setAction] = useState<'cancel' | 'reschedule'>();
  const [reason, setReason] = useState('');
  const [time, setTime] = useState(toSouthAfricanDateTimeInputValue(version.effectiveFrom));
  const [effectiveDate = '', effectiveTime = ''] = time.split('T');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (version.status !== 'SCHEDULED') return null;
  const label = pricingVersionLabel(version);
  const blocked = busy || disabled;
  return <section aria-label={`Schedule actions for ${label}`} className="my-2">
    {!action ? <>
      <button type="button" className="btn btn-outline-danger btn-sm me-2" disabled={blocked}
        onClick={() => { setError(''); setAction('cancel'); }}>Cancel schedule</button>
      <button type="button" className="btn btn-outline-primary btn-sm" disabled={blocked}
        onClick={() => { setError(''); setAction('reschedule'); }}>Reschedule</button>
    </> : <form onSubmit={async event => {
      event.preventDefault(); if (blocked || !reason.trim()) return;
      const instant = action === 'reschedule' && effectiveDate && effectiveTime ? fromSouthAfricanDateTimeInputValue(time) : undefined;
      if (action === 'reschedule' && !instant) { setError('Choose a valid effective date and time (SAST).'); return; }
      setBusy(true); onBusy(true); setError('');
      try {
        if (action === 'cancel') {
          await cancelPricingSchedule(version.versionId, reason.trim());
          onChanged(`Schedule cancelled: ${label}. It remains in the pricing history.`);
        } else {
          const replacement = await reschedulePricing(version.versionId, instant!, reason.trim(), name.trim() || undefined);
          onChanged(`Schedule replaced: ${pricingVersionLabel(replacement)}; effective ${formatSouthAfricanDateTime(replacement.policy.effectiveFrom!)}. The original remains in history.`);
        }
      } catch (e) { setError(pricingScheduleError(e)); }
      finally { setBusy(false); onBusy(false); }
    }}>
      <p>{action === 'cancel' ? 'Cancel this pending version before it becomes effective.'
        : 'Create a replacement schedule with these same pricing terms. The original will be cancelled only if the replacement succeeds.'}
        {' '}Other scheduled versions remain unchanged.</p>
      <fieldset disabled={blocked}>
        {action === 'reschedule' && <>
          <label className="form-label">New effective date
            <input className="form-control" type="date" required value={effectiveDate}
              onChange={event => setTime(`${event.target.value}T${effectiveTime}`)} /></label>
          <label className="form-label ms-2">Time (SAST)
            <input className="form-control" type="time" required value={effectiveTime}
              onChange={event => setTime(`${effectiveDate}T${event.target.value}`)} /></label>
          <label className="form-label ms-2">Replacement version name (optional)
            <input className="form-control" maxLength={80} value={name} placeholder="Generate automatically" onChange={event => setName(event.target.value)} /></label>
        </>}
        <label className="form-label d-block">Reason for {action === 'cancel' ? 'cancelling' : 'rescheduling'}
          <input className="form-control" required value={reason} onChange={event => setReason(event.target.value)} /></label>
        <button className="btn btn-primary btn-sm me-2" type="submit" disabled={!reason.trim() || (action === 'reschedule' && (!effectiveDate || !effectiveTime))}>
          {busy ? 'Saving schedule…' : action === 'cancel' ? 'Confirm cancellation' : 'Confirm reschedule'}</button>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { setAction(undefined); setError(''); }}>Back</button>
      </fieldset>
      {error && <p role="alert">{error}</p>}
    </form>}
  </section>;
}
